import { Button, radius, shadows } from '@alison/ui';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import type { AdNetworkStatus, AdProvider } from '../../lib/adNetwork.hooks.js';
import { deriveConnectionMetrics } from './integrationStatus.js';
import { NetworkMark } from './NetworkMark.js';
import { PROVIDER_DESCRIPTION, PROVIDER_LABEL } from './networks.js';
import { StatusChip } from './StatusChip.js';

interface ConnectionCardProps {
  provider: AdProvider;
  status: AdNetworkStatus;
  linkPending: boolean;
  onAddAccounts: () => void;
  onManage: () => void;
  onReconnect: () => void;
}

export function ConnectionCard({
  provider,
  status,
  linkPending,
  onAddAccounts,
  onManage,
  onReconnect,
}: ConnectionCardProps): ReactNode {
  const metrics = deriveConnectionMetrics(status);
  const failed = status.submissions.filter((submission) => submission.error);

  return (
    <Paper
      elevation={0}
      data-testid={`provider-card-${provider}`}
      sx={{
        px: { xs: 3, md: 5 },
        py: 4,
        border: '1px solid',
        borderColor: failed.length > 0 ? 'warning.main' : 'divider',
        borderRadius: `${radius.xl}px`,
        boxShadow: shadows.card,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 4, alignItems: { md: 'center' } }}>
        <NetworkMark provider={provider} size={46} />
        <Stack sx={{ flex: 1, minWidth: 0, gap: 2 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {PROVIDER_LABEL[provider]}
            </Typography>
            <StatusChip state={status.state} provider={provider} />
          </Stack>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            {PROVIDER_DESCRIPTION[provider]}
          </Typography>
          {metrics.total > 0 && (
            <Stack direction="row" sx={{ gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography sx={{ fontSize: 13 }}>
                <Box
                  component="span"
                  sx={{
                    mr: 1,
                    color: 'text.secondary',
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  Accounts
                </Box>
                {metrics.connected > 0 && `${metrics.connected} connected`}
                {metrics.connected > 0 && metrics.syncing > 0 && ' · '}
                {metrics.syncing > 0 && `${metrics.syncing} syncing`}
              </Typography>
              {metrics.failed > 0 && (
                <Typography sx={{ color: 'warning.dark', fontSize: 11, fontWeight: 700 }}>
                  {metrics.failed} needs attention
                </Typography>
              )}
            </Stack>
          )}
          {status.state === 'needs_reauth' && (
            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
              Access expired - reconnect to resume syncing.
            </Typography>
          )}
          {failed.map((submission) => (
            <Alert
              key={submission.provider_account_id}
              severity="error"
              data-testid={`submission-error-${submission.provider_account_id}`}
              sx={{ mt: 2 }}
            >
              {submission.provider_account_id}: {submission.error}
            </Alert>
          ))}
        </Stack>
        <Stack
          direction="row"
          sx={{ gap: 2, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}
        >
          {status.state === 'needs_reauth' ? (
            <Button
              variant="gradient"
              size="small"
              data-testid={`reconnect-${provider}`}
              disabled={linkPending}
              onClick={onReconnect}
            >
              {linkPending ? 'Redirecting…' : 'Reconnect'}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                size="small"
                data-testid={`add-accounts-${provider}`}
                disabled={linkPending}
                onClick={onAddAccounts}
              >
                {linkPending ? 'Redirecting…' : '+ Add ad accounts'}
              </Button>
              <Button
                variant="gradient"
                size="small"
                data-testid={`manage-${provider}`}
                onClick={onManage}
              >
                Manage
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
